import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import moment from "moment";
import {
  FiPhone,
  FiMessageCircle,
  FiCheck,
  FiCircle,
  FiStar,
  FiBell,
  FiRefreshCw,
} from "react-icons/fi";

// =======================================================
// AppointmentCard
// -------------------------------------------------------
// Displays a single appointment and its quick actions.
//
// Home.js still owns all business actions and passes them
// into this component as callbacks.
// =======================================================

const AppointmentCard = ({
  appointment,
  day,
  locations,
  selectedLocationId,
  groupedDays,
  openAppointmentActionsId,
  appointmentToMove,
  rescheduleMode,
  onToggleActions,
  onEdit,
  onOpenImages,
  onToggleConfirmation,
  onStartReschedule,
  onScheduleRetouch,
  onOpenProject,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const descriptionRef = useRef(null);
  const descriptionId = `calendar-description-${appointment.id}`;

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [appointment.id, appointment.description]);

  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element || descriptionExpanded) return;

    const measure = () => {
      setDescriptionOverflows(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(measure)
      : null;
    observer?.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [appointment.id, appointment.description, descriptionExpanded]);

  const cleanPhone = appointment.phone
    ?.replace(/\D/g, "")
    .replace(/^0/, "49");

  const reviewLink =
    Number(selectedLocationId) === 1
      ? "https://g.page/r/CZTIes1Eoox7EBM/review"
      : Number(selectedLocationId) === 2
        ? "https://g.page/r/CZBuZDbyIX6SEBM/review"
        : Number(selectedLocationId) === 3
          ? "https://g.page/r/Cc8nu4eJP43wEBM/review"
          : null;

  const openReminder = (event) => {
    event.stopPropagation();

    const locationCity =
      locations.find(
        (location) =>
          Number(location.id) === Number(selectedLocationId)
      )?.name || "deiner Stadt";

    const monthKey = moment(day.day).format("YYYY-MM");

    const artistGroup =
      groupedDays[monthKey]?.artists?.[day.artistName];

    const customerAppointmentsThisMonth = artistGroup
      ? artistGroup.dates
        .flatMap((date) =>
          (date.appointments || []).map((item) => ({
            ...item,
            dayDate: date.day,
          }))
        )
        .filter(
          (item) =>
            item.clientName?.toLowerCase() ===
            appointment.clientName?.toLowerCase() &&
            moment(item.dayDate).isSame(moment(day.day), "month") &&
            moment(item.dayDate).isSameOrAfter(moment(), "day")
        )
      : [];

    const firstName =
      appointment.clientName?.split(" ")[0] || "Hallo";

    const isRetouch = (item) =>
      /\b(nachstechen|nachstechtermin|touchup)\b/i.test(item.description || "");

    const retouchReminder = isRetouch(appointment);
    const reminderAppointments = customerAppointmentsThisMonth.filter(
      (item) => isRetouch(item) === retouchReminder
    );
    const reminderLines = reminderAppointments.length
      ? reminderAppointments.map((item) =>
          `• ${moment(item.dayDate).format("DD.MM.YYYY")} um ${moment(item.time, "HH:mm:ss").format("HH:mm")} Uhr`
        ).join("\n")
      : `• ${moment(day.day).format("DD.MM.YYYY")} um ${moment(appointment.time, "HH:mm:ss").format("HH:mm")} Uhr`;

    const introLine = retouchReminder
      ? (reminderAppointments.length > 1
          ? "Kleine Erinnerung an deine Nachstechtermine:"
          : "Kleine Erinnerung an deinen Nachstechtermin:")
      : (reminderAppointments.length > 1
          ? "Kleiner Reminder an deine bevorstehenden Tattoo-Termine:"
          : "Kleiner Reminder an deinen bevorstehenden Tattoo-Termin:");

    const reminderMessage = `Hey ${firstName}, hier ist Theo von Prime Ink Tattoo in ${locationCity}!

${introLine}

${reminderLines}

${retouchReminder ? "" : "Bitte iss vor unserem Termin etwas und bring die Bezahlung bitte in bar mit.\n\n"}Wir freuen uns auf dich ${String.fromCodePoint(0x1F340)}`;

    window.open(
      `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(
        reminderMessage
      )}`,
      "_blank"
    );
  };

  const projectImages = Array.isArray(
    appointment.images
  )
    ? appointment.images
    : [];

  const legacyImageUrls =
    appointment.consultation_image_url
      ? appointment.consultation_image_url
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean)
      : [];

  const galleryImages = [
    ...projectImages.map((image) => ({
      id: `project-${image.id}`,
      fullUrl: image.url,
      thumbnailUrl:
        image.thumbnailUrl || image.url,
      isProjectImage: true,
    })),

    ...legacyImageUrls.map((url, index) => ({
      id: `legacy-${index}-${url}`,
      fullUrl: url,
      thumbnailUrl: url,
      isProjectImage: false,
    })),
  ];

  const fullImageUrls = galleryImages.map(
    (image) => image.fullUrl
  );

  return (
    <div
      className={`
        appointment-item
        ${appointment.confirmed ? "appointment-confirmed" : ""}
        ${appointmentToMove?.id === appointment.id
          ? "appointment-reschedule-active"
          : ""
        }
      `}
      data-appointment-id={appointment.id}
      onClick={(event) => {
        event.stopPropagation();

        if (!rescheduleMode) {
          onEdit(appointment, day.dayId, day.day);
        }
      }}
    >
      <div className="appointment-header">
        <div className="client-name">
          {appointment.time
            ? moment(appointment.time, "HH:mm:ss").format("HH:mm")
            : "⏱️"}{" "}
          <span className="client-name-uppercase">
            {appointment.clientName}
          </span>
        </div>

        <button
          type="button"
          className="appointment-more-button"
          onClick={(event) => {
            event.stopPropagation();

            const isOpening =
              openAppointmentActionsId !== appointment.id;

            onToggleActions(appointment.id);

            if (isOpening) {
              setTimeout(() => {
                const card = document.querySelector(
                  `[data-appointment-id="${appointment.id}"]`
                );

                if (!card) return;

                // Scrollbarer Bereich innerhalb des Tages
                const appointmentsList =
                  card.closest(".appointments-list");

                if (appointmentsList) {
                  const cardRect = card.getBoundingClientRect();
                  const listRect =
                    appointmentsList.getBoundingClientRect();

                  // Falls die aufgeklappte Card unten aus
                  // der Terminliste herausragt
                  if (cardRect.bottom > listRect.bottom) {
                    appointmentsList.scrollBy({
                      top: cardRect.bottom - listRect.bottom + 12,
                      behavior: "smooth",
                    });
                  }

                  // Falls die Card oben außerhalb liegt
                  if (cardRect.top < listRect.top) {
                    appointmentsList.scrollBy({
                      top: cardRect.top - listRect.top - 12,
                      behavior: "smooth",
                    });
                  }
                }

                // Danach prüfen, ob der gesamte Tagesbereich
                // auch im Browserfenster sichtbar ist
                setTimeout(() => {
                  const rect = card.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;

                  if (rect.bottom > viewportHeight - 20) {
                    window.scrollBy({
                      top: rect.bottom - viewportHeight + 30,
                      behavior: "smooth",
                    });
                  }
                }, 200);
              }, 50);
            }
          }}
          title="Aktionen"
        >
          ⋯
        </button>
      </div>

      {onOpenProject && appointment.project_id && <button type="button" className="calendar-project-button"
        onClick={event => { event.stopPropagation(); onOpenProject(appointment); }}>
        {appointment.project_id
          ? `${appointment.project_title} · Session ${appointment.project_session_number}/${appointment.project_session_count}`
          : 'Als Projekt zusammenfassen'}
      </button>}
      {appointment.project_session_note && <div className="calendar-project-note">Session-Notiz: {appointment.project_session_note}</div>}

      <div className="appointment-description">
        {/\b(nachstechen|touchup)\b/i.test(appointment.description || "") && <span className="appointment-retouch-badge">Nachstechen</span>}
        <div
          ref={descriptionRef}
          id={descriptionId}
          className={`calendar-description-text${descriptionExpanded ? " is-expanded" : ""}`}
        >
          {appointment.description || <em>Keine Beschreibung</em>}
        </div>
        {descriptionOverflows && (
          <button
            type="button"
            className="calendar-description-toggle"
            aria-expanded={descriptionExpanded}
            aria-controls={descriptionId}
            onClick={(event) => {
              event.stopPropagation();
              setDescriptionExpanded((expanded) => !expanded);
            }}
          >
            {descriptionExpanded ? "Weniger anzeigen" : "Mehr anzeigen"}
          </button>
        )}
      </div>

      {galleryImages.length > 0 && (
        <div
          className="appointment-project-gallery"
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          {galleryImages
            .slice(0, 3)
            .map((image, index) => {
              const remainingCount =
                galleryImages.length - 3;

              const showRemainingCount =
                index === 2 &&
                remainingCount > 0;

              return (
                <button
                  key={image.id}
                  type="button"
                  className={`appointment-project-thumb ${index === 0
                      ? "is-cover"
                      : ""
                    }`}
                  onClick={(event) => {
                    event.stopPropagation();

                    onOpenImages?.(
                      fullImageUrls,
                      index
                    );
                  }}
                  aria-label={`Projektbild ${index + 1
                    } öffnen`}
                >
                  <img
                    src={image.thumbnailUrl}
                    alt=""
                    loading="lazy"
                  />

                  {index === 0 &&
                    image.isProjectImage && (
                      <span className="appointment-cover-dot" />
                    )}

                  {showRemainingCount && (
                    <span className="appointment-image-more">
                      +{remainingCount}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      )}

      {openAppointmentActionsId === appointment.id && (
        <div className="appointment-actions-expanded">
          {onOpenProject && !appointment.project_id && <button type="button" className="calendar-project-button"
            onClick={event => { event.stopPropagation(); onOpenProject(appointment); }}>Als Projekt zusammenfassen</button>}

          {onScheduleRetouch && <button type="button" className="appointment-retouch-action" onClick={(event) => { event.stopPropagation(); onScheduleRetouch(appointment, day); }}><FiRefreshCw /> Nachstechen planen</button>}
          <a href={`tel:${appointment.phone}`} title="Anrufen">
            <FiPhone />
          </a>

          {cleanPhone && cleanPhone.length >= 11 && (
            <a
              href={`https://wa.me/${cleanPhone}`}
              target="_blank"
              rel="noopener noreferrer"
              title="WhatsApp"
            >
              <FiMessageCircle />
            </a>
          )}

          {cleanPhone && cleanPhone.length >= 11 && reviewLink && (
            <a
              href={`https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(
                `Hey ${appointment.clientName}! Wir würden uns über deine Bewertung freuen:\n\n${reviewLink}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Bewertung"
            >
              <FiStar />
            </a>
          )}

          {cleanPhone && cleanPhone.length >= 11 && (
            <button
              type="button"
              onClick={openReminder}
              title="Reminder"
            >
              <FiBell />
            </button>
          )}

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleConfirmation(appointment.id);
              onToggleActions(appointment.id);
            }}
            title="Bestätigen"
          >
            {appointment.confirmed ? <FiCheck /> : <FiCircle />}
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();

              onStartReschedule(
                {
                  ...appointment,
                  originalDay: day.day,
                  originalArtistName: day.artistName,
                },
                day.dayId || day.id
              );
            }}
            title="Umbuchen"
          >
            ⇄
          </button>
        </div>
      )}
    </div>
  );
};

export default AppointmentCard;